import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import FeedbackButton from "lib/components/button/FeedbackButton";
import { Form } from "lib/components/form/FormTypes";
import TextInput from "lib/components/form/TextInput";

import { useAuth } from "authentication/AuthProvider";
import OAuth2LoginButton from "authentication/OAuth2LoginButton";

import AuthenticationClient from "authentication/AuthenticationClient";
import { Login } from "authentication/AuthenticationTypes";
import { asTitle, isEmailValid, isEmpty } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";

export default function LoginSection(): JSX.Element {
	const { t } = useTranslation();
	const { updateTokens } = useAuth();

	const [loginFailed, setLoginFailed] = useState(false);

	const { call } = useFetch();

	const [form, setForm] = useState<Form>({
		email: { value: "", required: true },
		password: { value: "", required: true }
	});

	const submit = useCallback((): Promise<void> => {
		form.email.error = !isEmailValid(form.email.value);
		form.password.error = isEmpty(form.password.value);

		// TODO: seems a very common construction.
		// Can we integrate this in a custom hook somehow?
		const hasError = Object.values(form).some(
			field => field.error === true
		);
		if (hasError) {
			setForm({ ...form });
			return Promise.resolve();
		} else {
			const data: Login = {
				email: form.email.value,
				password: form.password.value
			};
			// return login(form.email.value, form.password.value)
			return call(AuthenticationClient.login(data))
				.then(updateTokens)
				.then(() => setLoginFailed(false))
				.catch(() => setLoginFailed(true));
		}
	}, [form, updateTokens]);

	return (
		<>
			<Typography variant="h5" gutterBottom>
				{asTitle(t("login"))}
			</Typography>
			{loginFailed && (
				<Alert sx={{ textAlign: "left" }} severity="error">
					{t("invalidLogin")}
				</Alert>
			)}

			<Box
				component="form"
				sx={{
					"& .MuiTextField-root, .MuiButton-root, .MuiFormControl-root":
						{
							marginTop: 1,
							marginBottom: 1
						}
				}}>
				<TextInput
					aria-label="email"
					type="email"
					label={asTitle(t("email"))}
					field={form.email}
				/>
				<TextInput
					aria-label="password"
					type="password"
					label={asTitle(t("password"))}
					field={form.password}
				/>
				<FeedbackButton
					label={t("login")}
					variant="contained"
					onClick={submit}
					fullWidth
				/>
			</Box>

			<Typography variant="body1">
				<Link to="/request-password-reset">{t("resetPassword")}</Link>
			</Typography>

			<Typography variant="body1">
				<Link to="/register">{t("createAccount")}</Link>
			</Typography>

			<Divider sx={{ marginTop: 2, marginBottom: 1 }} />

			<div>
				<OAuth2LoginButton provider="google" />
			</div>
			{/* 
			<div>
				<OAuth2LoginButton provider="microsoft" />
			</div>
			*/}
		</>
	);
}
